import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom';

import { Grid, List, ListItem, ListItemText } from '@material-ui/core';

import MenuIcon from '@material-ui/icons/Menu';

import { ThemeNestedMenu, ThemeLoader } from 'src/theming';
import { useAbilityCtx } from 'src/roleAccesses';

import { useHttpClient } from 'src/lib/http-client/use-http-client';

import {
    useStyle,
    useContractDetailsViewDataStyle,
} from '../contracts-details-view-styles';

import { ContractDetailsListItem } from '../contract-details-list-item';

import { generateContractsDetailsAdditionalMenu } from '../contract-details-view-configs';

import { ContractDetailsViewLvrResponseBody } from './contract-details-view-lvr-response-body';

interface ContractDetailsViewLvrDetailsProps {
    handleOpenContractEdit: () => void;
    hideThemeNestedMenu: boolean;
    contractNumber: string;
}
export const ContractDetailsViewLvrDetails = (
    props: ContractDetailsViewLvrDetailsProps
): JSX.Element => {
    const ability = useAbilityCtx();
    const { t } = useTranslation(['contract-details-view']);

    const classes = useStyle();
    const contractDetailsViewDataClasses = useContractDetailsViewDataStyle();

    const { handleOpenContractEdit, hideThemeNestedMenu, contractNumber } = props;

    const router = useHistory();
    const { id } = useParams<{ id: string }>();
    const httpClient = useHttpClient();
    const [isLoadingDetails, setLoadingDetails] = useState(false);
    const [detailsData, setDetailsData] = useState<ContractDetailsViewLvrResponseBody>();
    const makeRequestForDetails =
        async (): Promise<ContractDetailsViewLvrResponseBody> => {
            setLoadingDetails(true);
            return httpClient.get<ContractDetailsViewLvrResponseBody>(
                `contracts/${id}/lvr`
            );
        };
    useEffect(() => {
        makeRequestForDetails()
            .then((data: ContractDetailsViewLvrResponseBody) => {
                setDetailsData(data);
            })
            .finally(() => {
                setLoadingDetails(false);
            });
    }, []);

    const goTo = (pathname: string): void => {
        router.push(pathname);
    };

    const menuItems = useMemo(
        () =>
            generateContractsDetailsAdditionalMenu(
                t,
                null,
                handleOpenContractEdit,
                goTo,
                ability,
                id,
                contractNumber
            ),
        [t, contractNumber, ability]
    );

    const isDetailDataEmpty = useMemo(() => {
        if (detailsData) {
            const detailsDataKeys = Object.keys(detailsData);
            return !detailsDataKeys?.map((x) => detailsData[x])?.filter((x) => x)?.length;
        }
        return true;
    }, [detailsData]);

    return (
        <Grid container>
            <Grid container className={classes.titleAndMenuContainer}>
                <div style={{ flexGrow: 1 }} />
                {!hideThemeNestedMenu &&
                    Array.isArray(menuItems) &&
                    menuItems.length > 0 && (
                        <ThemeNestedMenu
                            menuItems={menuItems}
                            buttonClass={classes.additionalMenuButton}
                            buttonIcon={<MenuIcon className={classes.menuIcon} />}
                        />
                    )}
            </Grid>
            {isLoadingDetails && <ThemeLoader />}
            {isDetailDataEmpty && (
                <ListItem>
                    <ListItemText data-title>
                        {t('contract-details-view:noContractDetails')}
                    </ListItemText>
                </ListItem>
            )}
            {detailsData && !isLoadingDetails && (
                <>
                    <List className={contractDetailsViewDataClasses.list}>
                        <ContractDetailsListItem
                            value={detailsData?.insuredPerson}
                            hidden={!detailsData?.insuredPerson}
                            title='contract-details-view:contractDetails.insuredPerson'
                        />
                        <ContractDetailsListItem
                            value={
                                detailsData?.insuranceDurationHV
                                    ? String(detailsData?.insuranceDurationHV)
                                    : ''
                            }
                            hidden={
                                detailsData?.insuranceDurationHV === null ||
                                detailsData?.insuranceDurationHV === 0
                            }
                            title='contract-details-view:contractDetails.insuranceDurationHV'
                        />
                        <ContractDetailsListItem
                            value={detailsData?.endAgeHV}
                            hidden={!detailsData?.endAgeHV}
                            title='contract-details-view:contractDetails.endAgeHV'
                        />
                    </List>
                </>
            )}
        </Grid>
    );
};
